import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { Link, generatePath } from 'react-router-dom'
import { Table, Typography, Tag, Tooltip, Switch } from 'antd'
import styled from 'styled-components'
import { StopOutlined } from '@ant-design/icons'
import * as Icons from '@ant-design/icons'

import { SUBSCRIPTION_TIER } from '@qflow/constants'
import { colours } from '@qflow/theme'

import { Flag } from 'admin/lib/Icons/Flag'
import * as ROUTES from 'admin/routes'
import moment from 'moment'
import { CloseOutModal } from './CloseOutModal'

export function ProjectList({ projects, accountId }) {
  projects.map(project => (project.accountId = accountId))

  const [closeOutProject, setCloseOutProject] = useState(null)
  const [includeClosedOut, setIncludeClosedOut] = useState(false)

  // Filter projects based on closed out status
  const filteredProjects = includeClosedOut
    ? projects
    : projects.filter(project => !project.isClosedOut)

  const allColumns = useMemo(
    () => [
      {
        title: 'Project Name',
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (name, project) => (
          <>
            <Typography.Text>{name}</Typography.Text>
            <FeatureIcons project={project} />
          </>
        )
      },
      {
        title: 'ID',
        dataIndex: 'projectId',
        sorter: (a, b) => a.projectId.localeCompare(b.projectId)
      },
      {
        title: 'Subscription Tier',
        dataIndex: 'subscriptionTier',
        sorter: (a, b) => a.subscriptionTier.localeCompare(b.subscriptionTier),
        align: 'center',
        render: tier => <SubscriptionTier tier={tier} />,
        width: '8rem'
      },
      {
        title: includeClosedOut ? 'Closed Out' : '',
        dataIndex: 'isClosedOut',
        width: includeClosedOut ? '6rem' : '0',
        align: 'center',
        render: isClosedOut => {
          return isClosedOut ? (
            <Tooltip title="The Project is Closed Out and new tickets will not arrive. NOTE: Currently there is no prevention of new Connections being created.">
              <StopOutlined
                style={{ color: colours.RED, fontSize: '1.25rem' }}
              />
            </Tooltip>
          ) : null
        }
      },
      {
        title: 'Creation Date',
        dataIndex: 'createdAt',
        width: '10rem',
        render: createdAt => {
          return (
            <Typography.Text>
              {moment(createdAt).local().format('DD/MM/YYYY HH:mm')}
            </Typography.Text>
          )
        }
      },
      {
        title: 'Country',
        dataIndex: 'country',
        sorter: (a, b) => a.country.localeCompare(b.country),
        width: '9%',
        render: country => {
          return <Typography.Text>{country}</Typography.Text>
        }
      },
      {
        title: '',
        dataIndex: 'country',
        width: '2rem',
        render: country => {
          return (
            <Flag
              country={country}
              style={{ width: '28px', margin: '0 -16px -2px' }}
            />
          )
        }
      },
      {
        title: 'Time Zone',
        dataIndex: 'timeZone',
        sorter: (a, b) => a.timeZone.localeCompare(b.timeZone),
        width: '9%'
      },
      {
        title: 'Modules',
        dataIndex: 'moduleTags',
        width: '6rem',
        render: modules =>
          modules?.map(module =>
            module.isTagged ? (
              <Tag
                key={module.moduleTagDefinitionId}
                style={{ margin: '.25rem 0' }}
              >
                {module.moduleTagDefinitionName}{' '}
              </Tag>
            ) : (
              ' '
            )
          )
      },
      {
        title: 'Links',
        key: 'links',
        width: '14rem',
        render: project => (
          <div style={{ margin: '0 -16px' }}>
            <Typography.Text
              style={{ lineHeight: '1.25', margin: '2px', display: 'block' }}
            >
              <Link
                to={generatePath(ROUTES.CONNECTIONS.LIST, {
                  accountId: project.accountId,
                  projectId: project.projectId
                })}
              >
                Connections
              </Link>
            </Typography.Text>
            <Typography.Text
              style={{ lineHeight: '1.25', margin: '2px', display: 'block' }}
            >
              <Link
                to={generatePath(ROUTES.NOTIFICATIONS.LIST, {
                  accountId: project.accountId,
                  projectId: project.projectId
                })}
              >
                Notifications
              </Link>
            </Typography.Text>
            <Typography.Text
              style={{ lineHeight: '1.25', margin: '2px', display: 'block' }}
            >
              <Link
                to={generatePath(ROUTES.SMART_WASTE.EDIT, {
                  accountId: project.accountId,
                  projectId: project.projectId
                })}
              >
                SmartWaste
              </Link>
            </Typography.Text>
          </div>
        )
      },
      {
        title: 'Edit',
        key: 'edit',
        width: '7rem',
        render: project => (
          <div style={{ margin: '0' }}>
            <Typography.Text style={{ lineHeight: '1.5', display: 'block' }}>
              <Link
                to={generatePath(ROUTES.PROJECTS.EDIT, {
                  accountId: project.accountId,
                  projectId: project.projectId
                })}
              >
                Edit
              </Link>
            </Typography.Text>
            <Typography.Text
              style={{
                lineHeight: '1.5',
                marginTop: '1em',
                display: 'block'
              }}
            >
              <Link
                style={{ color: colours.RED }}
                onClick={() => {
                  setCloseOutProject(project)
                }}
              >
                Close Out
              </Link>
            </Typography.Text>
          </div>
        )
      }
    ],
    [includeClosedOut]
  )

  // Filter out falsy values (when includeClosedOut is false)
  const columns = allColumns.filter(Boolean)

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        margin: '0.5rem 0.75rem 0'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
          flexWrap: 'wrap',
          margin: '1rem 1rem 0 1rem'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Switch checked={includeClosedOut} onChange={setIncludeClosedOut} />
          <Typography.Text style={{ fontWeight: 600 }}>
            Include Closed Out
          </Typography.Text>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
            fontSize: '0.875rem'
          }}
        >
          <Typography.Text style={{ fontWeight: 500 }}>Key:</Typography.Text>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icons.AppstoreOutlined style={{ color: colours.GREEN }} />
            <Typography.Text style={{ fontSize: '0.875rem' }}>
              Extended Product Groups
            </Typography.Text>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icons.UnorderedListOutlined style={{ color: colours.BLUE }} />
            <Typography.Text style={{ fontSize: '0.875rem' }}>
              Uniclass Codes
            </Typography.Text>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icons.BulbOutlined style={{ color: colours.AMBER }} />
            <Typography.Text style={{ fontSize: '0.875rem' }}>
              Design Matches
            </Typography.Text>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icons.BulbOutlined style={{ color: '#E91E63' }} />
            <Typography.Text style={{ fontSize: '0.875rem' }}>
              Design Matches v2
            </Typography.Text>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icons.ApiOutlined style={{ color: '#9C27B0' }} />
            <Typography.Text style={{ fontSize: '0.875rem' }}>
              Autodesk
            </Typography.Text>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icons.ExperimentOutlined style={{ color: colours.TEAL }} />
            <Typography.Text style={{ fontSize: '0.875rem' }}>
              One Click LCA
            </Typography.Text>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icons.CloudSyncOutlined
              style={{ color: colours.CONSTRUCTION_ORANGE }}
            />
            <Typography.Text style={{ fontSize: '0.875rem' }}>
              Procore (Enabled)
            </Typography.Text>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icons.CloudSyncOutlined style={{ color: colours.MID_GREY }} />
            <Typography.Text style={{ fontSize: '0.875rem' }}>
              Procore (Disabled)
            </Typography.Text>
          </div>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={filteredProjects}
        rowKey={row => row.projectId}
        pagination={false}
        scroll={{ y: `calc(100vh - 260px)` }}
        sticky
        style={{ margin: '1rem 0 0' }}
      />
      {closeOutProject && (
        <CloseOutModal
          accountId={accountId}
          project={closeOutProject}
          onCancel={() => setCloseOutProject(null)}
          onSuccess={() => window.location.reload()}
        />
      )}
    </div>
  )
}

ProjectList.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object),
  accountId: PropTypes.string.isRequired
}

function FeatureIcons({ project }) {
  return (
    <div style={{ marginTop: '0.25rem' }}>
      {project.isExtendedProductGroupsEnabled && (
        <Tooltip title="Extended Product Groups Enabled">
          <Icons.AppstoreOutlined
            style={{ color: colours.GREEN, marginRight: '0.25rem' }}
          />
        </Tooltip>
      )}
      {project.isUniclassCodesEnabled && (
        <Tooltip title="Uniclass Codes Enabled">
          <Icons.UnorderedListOutlined
            style={{ color: colours.BLUE, marginRight: '0.25rem' }}
          />
        </Tooltip>
      )}
      {project.isDesignMatchesV2Enabled ? (
        <Tooltip title="Design Matches v2 Enabled">
          <Icons.BulbOutlined
            style={{ color: '#E91E63', marginRight: '0.25rem' }}
          />
        </Tooltip>
      ) : (
        project.isDesignMatchesEnabled && (
          <Tooltip title="Design Matches Enabled">
            <Icons.BulbOutlined
              style={{ color: colours.AMBER, marginRight: '0.25rem' }}
            />
          </Tooltip>
        )
      )}
      {project.isAutodeskEnabled && (
        <Tooltip title="Autodesk Enabled">
          <Icons.ApiOutlined
            style={{ color: '#9C27B0', marginRight: '0.25rem' }}
          />
        </Tooltip>
      )}
      {project.isOneClickLcaEnabled && (
        <Tooltip title="One Click LCA Enabled">
          <Icons.ExperimentOutlined
            style={{ color: colours.TEAL, marginRight: '0.25rem' }}
          />
        </Tooltip>
      )}
      {project.procoreConfiguration &&
        typeof project.procoreConfiguration === 'object' && (
          <Tooltip
            title={`Procore Integration${project.procoreConfiguration.isAlertSyncEnabled ? ' - Alert Sync Enabled' : ' - Alert Sync Disabled'}`}
          >
            <Icons.CloudSyncOutlined
              style={{
                color: project.procoreConfiguration.isAlertSyncEnabled
                  ? colours.CONSTRUCTION_ORANGE
                  : colours.MID_GREY,
                marginRight: '0.25rem'
              }}
            />
          </Tooltip>
        )}
    </div>
  )
}

function SubscriptionTier({ tier }) {
  let tagColour
  switch (tier) {
    case SUBSCRIPTION_TIER.LEGACY:
      tagColour = `${colours.ORANGE}CC`
      break
    case SUBSCRIPTION_TIER.CAPTURE:
      tagColour = `${colours.GREEN}99`
      break
    case SUBSCRIPTION_TIER.COMPLY:
      tagColour = `${colours.GREEN}BB`
      break
    case SUBSCRIPTION_TIER.CONSULT:
      tagColour = `${colours.GREEN}DD`
      break
    case SUBSCRIPTION_TIER.PIONEERS:
      tagColour = `${colours.AMBER}DD`
      break
    default:
      tagColour = `${colours.GREEN}DD`
  }
  return (
    <SubscriptionTierBadge strong={true} style={{ backgroundColor: tagColour }}>
      {tier}
    </SubscriptionTierBadge>
  )
}

export const SubscriptionTierBadge = styled(Typography.Text)`
  display: inline-block;
  background-color: ${colours.GREEN};
  color: ${colours.WHITE};
  padding: 1px 4px;
  border-radius: 5px;
  margin: 0 0 1px;
`
